import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { UserContext } from '~/app';
import { getData } from '~/utils/service';
import { FeatureFlagsSchema, type FeatureFlags } from './schemas';

export type FeatureFlagLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface FeatureFlagContextValue {
  flags: FeatureFlags;
  status: FeatureFlagLoadStatus;
}

const CLOSED_FLAGS: FeatureFlags = {
  providerCalendar: false,
  providerAvailabilitySearch: false,
  b2bOfferingListings: false,
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: CLOSED_FLAGS,
  status: 'idle',
});

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = UserContext.useSelector((state) => state.matches('$_AUTHENTICATED'));
  const userId = UserContext.useSelector((state) => state.context.userId);
  const providerId = UserContext.useSelector((state) => state.context.providerId);
  const identity = isAuthenticated ? `${userId ?? 'none'}:${providerId ?? 'none'}` : null;
  const [loaded, setLoaded] = useState<{
    identity: string | null;
    value: FeatureFlagContextValue;
  }>({
    identity: null,
    value: { flags: CLOSED_FLAGS, status: 'idle' },
  });

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setLoaded({ identity: null, value: { flags: CLOSED_FLAGS, status: 'idle' } });
      return () => {
        cancelled = true;
      };
    }

    setLoaded({ identity, value: { flags: CLOSED_FLAGS, status: 'loading' } });
    void getData<unknown>('featureFlags')
      .then((response) => {
        if (cancelled) return;
        setLoaded({
          identity,
          value: { flags: FeatureFlagsSchema.parse(response), status: 'ready' },
        });
      })
      .catch((error) => {
        console.debug('FeatureFlagProvider: failed to load feature flags', error);
        if (!cancelled) {
          setLoaded({ identity, value: { flags: CLOSED_FLAGS, status: 'error' } });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [identity, isAuthenticated]);

  const contextValue = useMemo<FeatureFlagContextValue>(() => {
    if (loaded.identity === identity) return loaded.value;
    return {
      flags: CLOSED_FLAGS,
      status: identity == null ? 'idle' : 'loading',
    };
  }, [identity, loaded]);
  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};
